// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@libraryadmin/components/ui/dialog";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@libraryadmin/components/ui/tabs";
import {
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import { getApprovals, updateApprovalStatus } from "@libraryadmin/services/api";
import type { Approval } from "@libraryadmin/data/seed";

const Approvals = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filterTab, setFilterTab] = useState<
    "pending" | "approved" | "rejected" | "all"
  >("pending");
  const [viewingApproval, setViewingApproval] = useState<Approval | null>(null);

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ["approvals"],
    queryFn: getApprovals,
  });

  const mutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "approved" | "rejected";
    }) => updateApprovalStatus(id, status),
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ["approvals"] });
      toast({
        title: status === "approved" ? "Request Approved" : "Request Rejected",
        description: `The request has been marked as ${status}.`,
        variant: status === "rejected" ? "destructive" : "default",
      });
      setViewingApproval(null);
    },
  });

  const filtered = approvals.filter(a => {
    if (filterTab === "all") {
      return true;
    }
    return a.status === filterTab;
  });

  const pendingCount = approvals.filter(a => a.status === "pending").length;

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filtered);

  return (
    <DashboardLayout
      title="Approvals & Authorization"
      subtitle="Review pending requests for member registrations, manuscript access, and restricted book issues"
    >
      <div className="space-y-6">
        {/* Header Tabs & Stats */}
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <Tabs
            value={filterTab}
            onValueChange={v => setFilterTab(v as typeof filterTab)}
          >
            <TabsList className="bg-muted/50 p-1">
              <TabsTrigger value="pending" className="gap-2">
                <Clock className="h-4 w-4 text-warning" /> Pending (
                {pendingCount})
              </TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all">
                All Logs ({approvals.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-card px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <span>Senior Librarian Approval Privileges Active</span>
          </div>
        </div>

        <div className="form-section animate-fade-in">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Authorization Queue</h2>
              <Badge variant="outline" className="badge-warning">
                {filtered.length} Requests
              </Badge>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Type</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Details & Reason</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No approval requests found in this view
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map(item => (
                    <TableRow key={item.id} className="table-row-hover">
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            item.type === "manuscript_access"
                              ? "badge-info"
                              : item.type === "book_issue"
                                ? "border-primary/20 bg-primary/10 text-primary"
                                : "border-accent/20 bg-accent/10 text-accent"
                          }
                        >
                          {item.type.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-semibold">
                        {item.requestedBy}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {item.details}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {item.requestedOn}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            item.priority === "high"
                              ? "badge-destructive"
                              : "badge-secondary"
                          }
                        >
                          {item.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            item.status === "approved"
                              ? "badge-success"
                              : item.status === "rejected"
                                ? "badge-destructive"
                                : "badge-warning"
                          }
                        >
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingApproval(item)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {item.status === "pending" && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-secondary hover:bg-secondary/10 hover:text-secondary"
                                onClick={() =>
                                  mutation.mutate({
                                    id: item.id,
                                    status: "approved",
                                  })
                                }
                                disabled={mutation.isPending}
                              >
                                <CheckCircle className="mr-1 h-4 w-4" />
                                Approve
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() =>
                                  mutation.mutate({
                                    id: item.id,
                                    status: "rejected",
                                  })
                                }
                                disabled={mutation.isPending}
                              >
                                <XCircle className="mr-1 h-4 w-4" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* View Detail Dialog */}
      <Dialog
        open={!!viewingApproval}
        onOpenChange={open => !open && setViewingApproval(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Approval Request Detail
            </DialogTitle>
            <DialogDescription>ID: {viewingApproval?.id}</DialogDescription>
          </DialogHeader>

          {viewingApproval && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/30 p-3">
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Request Type
                  </span>
                  <span className="font-semibold capitalize">
                    {viewingApproval.type.replace("_", " ")}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Requested By
                  </span>
                  <span className="font-semibold">
                    {viewingApproval.requestedBy}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Date Submitted
                  </span>
                  <span>{viewingApproval.requestedOn}</span>
                </div>
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Current Status
                  </span>
                  <Badge variant="outline" className="capitalize">
                    {viewingApproval.status}
                  </Badge>
                </div>
              </div>

              <div>
                <span className="mb-1 block text-xs font-medium text-muted-foreground">
                  Request Details:
                </span>
                <p className="rounded-lg border bg-card p-3 text-foreground">
                  {viewingApproval.details}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {viewingApproval?.status === "pending" && (
              <div className="flex w-full justify-end gap-2">
                <Button
                  variant="outline"
                  className="border-destructive/30 text-destructive"
                  onClick={() =>
                    mutation.mutate({
                      id: viewingApproval.id,
                      status: "rejected",
                    })
                  }
                >
                  <XCircle className="mr-2 h-4 w-4" /> Reject
                </Button>
                <Button
                  className="btn-primary"
                  onClick={() =>
                    mutation.mutate({
                      id: viewingApproval.id,
                      status: "approved",
                    })
                  }
                >
                  <CheckCircle className="mr-2 h-4 w-4" /> Approve Request
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Approvals;
